/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description React Hook usePaginatedProviders
 */

import _ from 'lodash';
import { useQuery, UseQueryResult } from '@tanstack/react-query';

import api from 'client/index';
import { getParsedProviderDetails } from 'utils/admin/providers';
import QUERY_KEYS from 'app-constants/queryKeys';
import { updateApiParamsCriteria } from 'utils/advancedFilterUtils';
import { PROVIDER_ADF_FIELD_MAP } from 'utils/adfUtils';

interface IModel {
	apiParams?: Provider.IApiParams;
	allProviders?: boolean;
	pageNumber?: number;
	pageSize?: number;
}

/**
 * usePaginatedProviders hook
 * @returns {UseQueryResult} usePaginatedProviders
 */
const usePaginatedProviders = (
	{ apiParams = {}, pageNumber, pageSize, allProviders = false }: IModel,
	options?: IQueryOptions,
	cacheKey?: string,
): UseQueryResult<Provider.IPaginatedProviderMeta, IAxiosError> => {
	return useQuery({
		queryKey: [QUERY_KEYS.provider, { apiParams, allProviders, pageNumber, pageSize, cacheKey }],
		queryFn: async () => {
			const { data } = await api.ProvidersApi.getProviders({
				providerSearchModel: updateApiParamsCriteria(_.cloneDeep(apiParams), PROVIDER_ADF_FIELD_MAP),
				allProviders,
				pgNum: pageNumber,
				pgSize: pageSize,
			});
			return {
				...data,
				data: _.map(data?.data, (provider) => ({
					...provider,
					...getParsedProviderDetails(provider?.fhirResource as Fhir.Practitioner | Fhir.Organization),
				})),
			};
		},
		...(options || {}),
	});
};

export default usePaginatedProviders;
